import { LongGameError } from '@long-game/common';
import { hcWithType as apiHc } from '@long-game/service-api/client';
import { API_ORIGIN } from '../config.js';
import { fetch } from '../fetch.js';
import { queryClient } from '../queryClient.js';
export const isQueryFactory = (factory) => {
    return factory.__isQuery === true;
};
export const isInfiniteQueryFactory = (factory) => {
    return factory.__isInfiniteQuery === true;
};
export class BaseSdk extends EventTarget {
    apiRpc;
    queryClient = queryClient;
    constructor() {
        super();
        this.apiRpc = apiHc(API_ORIGIN, { fetch });
    }
    sdkQuery = (key, fn, { transformInput, transformOutput, getKey, enabled, defaults, ignoreError, emptyResponse, } = {}) => {
        const factory = (...args) => {
            const input = transformInput ? args[0] : undefined;
            const options = transformInput
                ? args[1]
                : args[0];
            const queryFn = async () => {
                try {
                    const res = await fn(transformInput ? transformInput(input) : undefined);
                    if (res.status === 404 && emptyResponse !== undefined) {
                        return emptyResponse;
                    }
                    LongGameError.throwIfError(res);
                    const body = (await res.json());
                    const result = transformOutput
                        ? transformOutput(body)
                        : body;
                    return result;
                }
                catch (e) {
                    console.error(e);
                    const asLongGameError = LongGameError.wrap(e);
                    if (!ignoreError?.(asLongGameError)) {
                        this.dispatchEvent(new ErrorEvent('error', { error: asLongGameError }));
                    }
                    throw asLongGameError;
                }
            };
            const subkey = getKey ? getKey(input) : [input];
            return {
                queryFn,
                queryKey: [key, ...subkey],
                enabled: enabled ? enabled(input) : true,
                ...defaults,
                ...options,
            };
        };
        factory.__isQuery = true;
        factory.run = async (input) => {
            const result = await this.queryClient.fetchQuery(factory(...[input]));
            return result;
        };
        return factory;
    };
    manualQuery = (key, fn) => {
        const factory = (...args) => {
            const input = args[0];
            return {
                queryFn: () => fn(input),
                queryKey: [key, input],
            };
        };
        factory.__isQuery = true;
        factory.run = async (input) => {
            const result = await this.queryClient.fetchQuery(factory(...[input]));
            return result;
        };
        return factory;
    };
    sdkInfiniteQuery = (key, fn, { transformInput, transformOutput, getKey, enabled, defaults, } = {}) => {
        const factory = (...args) => {
            const input = args[0];
            const options = args[1];
            const queryFn = async ({ pageParam, }) => {
                try {
                    const res = await fn(transformInput ? transformInput(input) : undefined, pageParam);
                    LongGameError.throwIfError(res);
                    const body = (await res.json());
                    const result = transformOutput
                        ? transformOutput(body)
                        : body;
                    return result;
                }
                catch (e) {
                    console.error(e);
                    this.dispatchEvent(new ErrorEvent('error', { error: e }));
                    if (LongGameError.isInstance(e)) {
                        throw e;
                    }
                    throw new LongGameError(LongGameError.Code.Unknown, 'An error occurred. Please try again later.');
                }
            };
            const subkey = getKey ? getKey(input) : [input];
            return {
                queryFn,
                queryKey: [key, ...subkey],
                enabled: enabled ? enabled(input) : true,
                ...defaults,
                ...options,
                getNextPageParam: (lastPage) => {
                    if (lastPage.pageInfo) {
                        return lastPage.pageInfo.endCursor;
                    }
                    return undefined;
                },
                initialPageParam: undefined,
            };
        };
        factory.__isInfiniteQuery = true;
        factory.run = async (input) => {
            const result = await this.queryClient.fetchInfiniteQuery(factory(...[input]));
            return result;
        };
        return factory;
    };
    fetch = this.queryClient.fetchQuery.bind(this.queryClient);
    optimisticUpdate = (query, updater) => {
        this.queryClient.setQueryData(query.queryKey, updater);
    };
    sdkMutation = (fn, { transformInput, transformOutput, invalidate, onSuccess, } = {}) => {
        const mutationFn = async (args) => {
            try {
                const res = await fn(transformInput ? transformInput(args) : undefined);
                LongGameError.throwIfError(res);
                const body = (await res.json());
                const result = transformOutput
                    ? transformOutput(body)
                    : body;
                return result;
            }
            catch (e) {
                console.error(e);
                this.dispatchEvent(new ErrorEvent('error', { error: e }));
                if (LongGameError.isInstance(e)) {
                    throw e;
                }
                throw new LongGameError(LongGameError.Code.Unknown, 'An error occurred. Please try again later.');
            }
        };
        return {
            mutationFn,
            onSuccess: (output, vars) => {
                if (invalidate) {
                    for (const key of invalidate) {
                        this.queryClient.invalidateQueries({ queryKey: key });
                    }
                }
                onSuccess?.(output, vars);
            },
        };
    };
    manualMutation = (fn, { onSuccess, invalidate, } = {}) => {
        const mutationFn = async (args) => {
            try {
                return await fn(args);
            }
            catch (e) {
                console.error(e);
                if (LongGameError.isInstance(e)) {
                    throw e;
                }
                throw new LongGameError(LongGameError.Code.Unknown, 'An error occurred. Please try again later.');
            }
        };
        return {
            mutationFn,
            onSuccess: (data, vars, ctx) => {
                invalidate?.forEach((key) => {
                    this.queryClient.invalidateQueries({ queryKey: key });
                });
                onSuccess?.(data, vars);
            },
        };
    };
    run = async (mutation, input) => {
        const result = await mutation.mutationFn(input, undefined);
        mutation.onSuccess?.(result, input, undefined, undefined);
    };
}
//# sourceMappingURL=BaseSdk.js.map